import type { components } from '@/api/client';
import type {
  TestOrderSampleType,
  TestOrderStatus,
  TestOrderType,
  TestOrderViewModel,
} from '@/features/patient-record/types';
import { narrowEnum, nullableString } from '@/api/mappers/_shared';

type TestOrderDto = components['schemas']['TestOrder'];

const KNOWN_STATUS: ReadonlySet<TestOrderStatus> = new Set<TestOrderStatus>([
  'ORDERED',
  'SAMPLE_SENT',
  'RESULTED',
  'REVIEWED',
  'CANCELLED',
]);

const KNOWN_TEST_TYPE: ReadonlySet<TestOrderType> = new Set<TestOrderType>([
  'SINGLE_GENE',
  'GENE_PANEL',
  'WES',
  'WGS',
  'KARYOTYPE',
  'MICROARRAY',
  'MLPA',
  'OTHER',
]);

const KNOWN_SAMPLE_TYPE: ReadonlySet<TestOrderSampleType> = new Set<TestOrderSampleType>([
  'BLOOD',
  'SALIVA',
  'BUCCAL_SWAB',
  'TISSUE',
  'DRIED_BLOOD_SPOT',
  'OTHER',
]);

function mapSampleType(value: string | null | undefined): TestOrderSampleType | null {
  if (value === null || value === undefined) return null;
  return KNOWN_SAMPLE_TYPE.has(value as TestOrderSampleType)
    ? (value as TestOrderSampleType)
    : null;
}

export function mapTestOrder(dto: TestOrderDto): TestOrderViewModel {
  return {
    id: dto.id ?? '',
    testName: dto.test_name ?? '',
    testType: narrowEnum<TestOrderType>(dto.test_type, KNOWN_TEST_TYPE, 'OTHER'),
    testCode: nullableString(dto.test_code),
    panelName: nullableString(dto.panel_name),
    labProvider: nullableString(dto.lab_provider),
    indication: nullableString(dto.indication),
    orderedByUserId: nullableString(dto.ordered_by_user_id),
    orderedByExternal: nullableString(dto.ordered_by_external),
    orderedDate: dto.ordered_date ?? '',
    sampleType: mapSampleType(dto.sample_type),
    sampleCollectedDate: nullableString(dto.sample_collected_date),
    sampleSentDate: nullableString(dto.sample_sent_date),
    targetDeliveryDate: nullableString(dto.target_delivery_date),
    specimen: nullableString(dto.specimen),
    status: narrowEnum<TestOrderStatus>(dto.status, KNOWN_STATUS, 'ORDERED'),
    resultDate: nullableString(dto.result_date),
    resultSummary: nullableString(dto.result_summary),
    externalReportUrl: nullableString(dto.external_report_url),
    notes: nullableString(dto.notes),
    createdAt: nullableString(dto.created_at),
    modifiedOn: nullableString(dto.modified_on),
  };
}

export function mapTestOrders(dtos: readonly TestOrderDto[]): TestOrderViewModel[] {
  return dtos.map(mapTestOrder);
}
